import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Bold, Italic, List, Link2 } from 'lucide-react';

export function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'Enter description...',
  minHeight = 120,
  showLink = true,
  noBorder = false,
}) {
  const extensions = [
    StarterKit.configure({
      heading: false,
      blockquote: false,
      code: false,
      codeBlock: false,
      horizontalRule: false,
      orderedList: false,
      strike: false,
      hardBreak: true,
      link: false,
    }),
    ...(showLink ? [Link.configure({
      openOnClick: false,
      autolink: false,
      linkOnPaste: true,
      protocols: ['http', 'https', 'mailto'],
      HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer nofollow' },
    })] : []),
  ];

  const editor = useEditor({
    extensions,
    content: value || '<p></p>',
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  if (!editor) return <div className="rounded-lg p-3 text-sm text-gray-400">Loading editor…</div>;

  const addLink = () => {
    const prev = editor.getAttributes('link').href ?? '';
    const url = window.prompt('Enter URL:', prev);
    if (url === null) return;
    if (!url.trim()) { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().setLink({ href: url.trim() }).run();
  };

  const ToolBtn = ({ onClick, active, title, children }) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      className={`p-1.5 rounded text-sm transition-colors ${
        active ? 'bg-green-100 text-green-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className={`${noBorder ? '' : 'border border-gray-200'} rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-green-500 focus-within:border-transparent`}>
      <div className="flex gap-0.5 border-b border-gray-200 bg-gray-50 px-2 py-1.5">
        <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
          <Bold size={14} />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
          <Italic size={14} />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list">
          <List size={14} />
        </ToolBtn>
        {showLink && (
          <ToolBtn onClick={addLink} active={editor.isActive('link')} title="Add link">
            <Link2 size={14} />
          </ToolBtn>
        )}
      </div>
      <EditorContent
        editor={editor}
        style={{ minHeight }}
        className="rich-editor-content px-3 py-2 text-sm focus:outline-none"
      />
    </div>
  );
}
